import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MessageCircle, Plus, Crown, EyeOff } from "lucide-react";
import TipButton from "@/components/TipButton";

export default function Forum() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", track_slug: "" });
  const [activePost, setActivePost] = useState(null);
  const [reply, setReply] = useState("");

  const load = async () => {
    const [p, t] = await Promise.all([api.get("/forum"), api.get("/tracks")]);
    setPosts(p.data);
    setTracks(t.data);
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;
    await api.post("/forum", { ...form, track_slug: form.track_slug || null });
    setForm({ title: "", body: "", track_slug: "" });
    setOpen(false);
    toast.success("Thread posted.");
    load();
  };

  const submitReply = async (id) => {
    if (!reply.trim()) return;
    await api.post(`/forum/${id}/replies`, { body: reply });
    setReply("");
    load();
    const refreshed = (await api.get("/forum")).data.find((p) => p.id === id);
    setActivePost(refreshed);
    toast.success("Reply posted.");
  };

  return (
    <div className="bg-tcpi-bg min-h-screen" data-testid="forum-page">
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="overline mb-3">Each One Teaches One</div>
            <h1 className="text-4xl text-[#F7F5F0]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Round Table Forum
            </h1>
            <p className="text-[#8A8782] mt-2">
              Speak honestly. Listen first. Lead with humility.
            </p>
          </div>
          <button onClick={() => setOpen(!open)} className="btn-primary flex items-center gap-2" data-testid="new-thread-btn">
            <Plus className="w-4 h-4" /> New Thread
          </button>
        </div>

        {open && (
          <form onSubmit={submit} className="card-bento mb-8 space-y-4" data-testid="new-thread-form">
            <input
              required
              placeholder="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="input-tcpi"
              data-testid="thread-title-input"
            />
            <select
              value={form.track_slug}
              onChange={(e) => setForm({ ...form, track_slug: e.target.value })}
              className="input-tcpi"
              data-testid="thread-track-select"
            >
              <option value="">General (no track)</option>
              {tracks.map((t) => (
                <option key={t.slug} value={t.slug}>
                  Track {t.number}: {t.title}
                </option>
              ))}
            </select>
            <textarea
              required
              rows={5}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Share your thoughts honestly…"
              className="input-tcpi resize-none"
              data-testid="thread-body-input"
            />
            <button type="submit" className="btn-primary" data-testid="thread-submit-btn">
              Post
            </button>
          </form>
        )}

        <div className="space-y-4">
          {posts.length === 0 && (
            <div className="text-[#8A8782] text-center py-10">
              No threads yet. Start the conversation.
            </div>
          )}
          {posts.map((p) => (
            <div key={p.id} className="card-bento" data-testid={`post-${p.id}`}>
              <button
                onClick={() => setActivePost(activePost?.id === p.id ? null : p)}
                className="text-left w-full"
              >
                <div className="flex items-start justify-between gap-4 mb-2">
                  <h3
                    className="text-2xl text-[#F7F5F0]"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    {p.title}
                  </h3>
                  <span className="overline text-[10px]">{p.replies.length} replies</span>
                </div>
                <div className="text-xs text-[#C9A961] mb-3 flex items-center gap-2">
                  {p.author_anonymous && <EyeOff className="w-3 h-3" />}
                  {!p.author_anonymous && p.author_role === "facilitator" && <Crown className="w-3 h-3" />}
                  {p.author_name} · {p.author_role}
                  {p.track_slug && <span className="text-[#8A8782]">· {p.track_slug}</span>}
                </div>
                <p className="text-[#D1CFC9] leading-relaxed whitespace-pre-wrap">{p.body}</p>
              </button>
              <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                <TipButton
                  postId={p.id}
                  recipientId={p.author_id}
                  recipientProgram={p.author_program}
                  recipientRole={p.author_role}
                  recipientAnonymous={p.author_anonymous}
                  recipientName={p.author_name}
                  currentUser={user}
                />
              </div>

              {activePost?.id === p.id && (
                <div className="mt-6 pt-6 border-t border-[#C9A961]/15">
                  <div className="space-y-3 mb-4">
                    {p.replies.map((r) => (
                      <div key={r.id} className="p-3 bg-[#1A1816] rounded-md" data-testid={`reply-${r.id}`}>
                        <div className="text-xs text-[#C9A961] mb-1 flex items-center gap-2">
                          {r.author_anonymous && <EyeOff className="w-3 h-3" />}
                          {!r.author_anonymous && r.author_role === "facilitator" && <Crown className="w-3 h-3" />}
                          {r.author_name}
                        </div>
                        <div className="text-sm text-[#D1CFC9] whitespace-pre-wrap">{r.body}</div>
                        <div className="mt-2">
                          <TipButton
                            postId={p.id}
                            replyId={r.id}
                            recipientId={r.author_id}
                            recipientProgram={r.author_program}
                            recipientRole={r.author_role}
                            recipientAnonymous={r.author_anonymous}
                            recipientName={r.author_name}
                            currentUser={user}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={3}
                    className="input-tcpi resize-none mb-2"
                    placeholder="Your reply…"
                    data-testid="reply-input"
                  />
                  <button onClick={() => submitReply(p.id)} className="btn-primary text-sm" data-testid="reply-submit-btn">
                    Reply
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
